import { MSAxiosInstance as PhpMSAxiosInstance } from "@mobsuccess-devops/php-client/axios-instance";
import {
  createRouterAppRoot,
  getBusinessUnit,
} from "@mobsuccess-devops/react-shared";
import { unregister } from "@mobsuccess-devops/react-shared/service-worker";
import type { QueryClient } from "@tanstack/react-query";
import type { createRouter, RouterHistory } from "@tanstack/react-router";

import { AuthLayer } from "./AuthLayer";
import { applyLegacyActionUrl } from "./features/auth/apply-legacy-action-url";
import { detachPlatformSignoutListener } from "./features/auth/detach-platform-signout-listener";
import {
  applyPartnerBrand,
  detectPartnerBrand,
} from "./features/brand/partner-brand";
import { entrypoint } from "./features/constants/entrypoint";
import { sentryOptions } from "./features/sentry";
import { applyTheme, readStoredTheme } from "./features/theme/theme";
import "./index.css";
import "./mobyone.css";
import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<
      typeof createRouter<
        typeof routeTree,
        "never",
        false,
        RouterHistory,
        { queryClient: QueryClient }
      >
    >;
  }
}

// Before the first paint, so a stored choice never flashes the other theme.
applyTheme(readStoredTheme());

// Before the router boots: its first navigation drops `to` from the URL.
applyPartnerBrand(detectPartnerBrand(window.location));

applyLegacyActionUrl(
  typeof window._msBaseUrl === "string" ? window._msBaseUrl : "/",
);

detachPlatformSignoutListener();

createRouterAppRoot({
  routing: {
    routeTree,
    defaultRoute: "/",
  },
  businessUnit: getBusinessUnit(),
  entrypoint,
  disableEnsureUser: true,
  sentry: sentryOptions,
  lingui: {
    defaultLanguage: "fr",
    locales: import.meta.glob(
      ["./locales/*.json", "!./locales/messages.json"],
      { eager: true, query: "?lingui" },
    ),
  },
  auth: {
    // This app owns the full login flow (AuthLayer). Every other webapp gets
    // the thin SessionProvider and redirects here to authenticate.
    AuthLayer,
    userPoolsWebClientId:
      window._msAuth?.userPool?.userPoolsWebClientId ??
      import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
  },
  axios: [
    {
      php: {
        instance: PhpMSAxiosInstance,
        base: import.meta.env.VITE_MS_API_ENDPOINT,
      },
    },
    { cacheBusting: true },
  ],
});

unregister();
